import React, { useState, useEffect } from 'react';
import { Globe, Eye, EyeOff, Save, ExternalLink } from 'lucide-react';
import { trustPortalApi, TrustPortalSettings as Settings } from '../api/trustPortal.api';
import PageInfoButton from '@/components/PageInfoButton';

const DEFAULT_SETTINGS: Partial<Settings> = {
  slug: '',
  is_public: false,
  show_certifications: true,
  show_frameworks: true,
  show_compliance_score: true,
  show_last_audit_date: true,
  show_controls_count: false,
  show_emission_targets: false,
  show_scope_breakdown: false,
  show_carbon_credits: false,
  show_emission_offsets: false,
  show_sustainability_score: false,
  custom_headline: '',
  custom_description: '',
  accent_color: '#059669',
};

const TrustPortalSettings: React.FC = () => {
  const [settings, setSettings] = useState<Partial<Settings>>({ ...DEFAULT_SETTINGS });
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [slugError, setSlugError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await trustPortalApi.getSettings();
        if (res) setSettings({ ...DEFAULT_SETTINGS, ...res });
      } catch {
        // No settings yet, use defaults
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const validateSlug = (value: string) => {
    if (!value) { setSlugError('Slug is required'); return false; }
    if (!/^[a-z0-9-]+$/.test(value)) {
      setSlugError('Only lowercase letters, numbers and hyphens allowed');
      return false;
    }
    setSlugError('');
    return true;
  };

  const handleSave = async () => {
    if (!validateSlug(settings.slug || '')) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const updated = await trustPortalApi.saveSettings(settings);
      setSettings({ ...DEFAULT_SETTINGS, ...updated });
      setSuccess('Settings saved successfully.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const publicUrl = settings.slug ? `${window.location.origin}/trust/${settings.slug}` : '';

  if (isLoading) return (
    <div className="flex items-center justify-center h-64 text-slate-500">Loading...</div>
  );

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <div className="flex items-center gap-3">
        <Globe className="w-7 h-7 text-emerald-600" />
        <div>
          <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Trust Portal</h1><PageInfoButton /></div>
          <p className="text-slate-500 text-sm mt-0.5">Publish your compliance status publicly for customers and partners</p>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}
      {success && <div className="bg-green-50 text-green-700 px-4 py-3 rounded-lg text-sm">{success}</div>}

      {/* Portal URL */}
      <div className="bg-white border rounded-xl p-5 space-y-4">
        <h2 className="font-semibold text-slate-800">Portal URL</h2>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Slug (URL identifier)</label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400 whitespace-nowrap">{window.location.origin}/trust/</span>
            <input
              value={settings.slug || ''}
              onChange={e => {
                const v = e.target.value.toLowerCase();
                setSettings(s => ({ ...s, slug: v }));
                validateSlug(v);
              }}
              placeholder="your-company-name"
              className={`flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${slugError ? 'border-red-400' : ''}`}
            />
          </div>
          {slugError && <p className="text-xs text-red-500 mt-1">{slugError}</p>}
          {publicUrl && !slugError && (
            <p className="text-xs text-slate-400 mt-1">{publicUrl}</p>
          )}
        </div>
      </div>

      {/* Visibility */}
      <div className="bg-white border rounded-xl p-5">
        <h2 className="font-semibold text-slate-800 mb-4">Visibility</h2>
        <div
          onClick={() => setSettings(s => ({ ...s, is_public: !s.is_public }))}
          className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
            settings.is_public ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
          }`}
        >
          <div className="flex items-center gap-3">
            {settings.is_public ? (
              <Eye className="w-5 h-5 text-emerald-600" />
            ) : (
              <EyeOff className="w-5 h-5 text-slate-400" />
            )}
            <div>
              <div className="font-medium text-slate-900">{settings.is_public ? 'Public' : 'Private'}</div>
              <div className="text-xs text-slate-500">
                {settings.is_public ? 'Anyone with the link can view your trust portal' : 'Portal is not visible to the public'}
              </div>
            </div>
          </div>
          <div className={`w-12 h-6 rounded-full transition-colors ${settings.is_public ? 'bg-emerald-500' : 'bg-slate-300'}`}>
            <div className={`w-5 h-5 rounded-full bg-white shadow mt-0.5 transition-transform ${settings.is_public ? 'translate-x-6 ml-0.5' : 'translate-x-0.5'}`} />
          </div>
        </div>

        {settings.is_public && publicUrl && (
          <a href={publicUrl} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 mt-3 text-sm text-emerald-600 hover:underline">
            <ExternalLink className="w-4 h-4" /> Preview your trust portal
          </a>
        )}
      </div>

      {/* Display Options — Compliance */}
      <div className="bg-white border rounded-xl p-5 space-y-3">
        <h2 className="font-semibold text-slate-800 mb-2">Compliance Display</h2>
        <p className="text-xs text-slate-500 mb-3">Choose which compliance data to show on your public trust portal.</p>
        {[
          { key: 'show_certifications', label: 'Active certifications', desc: 'ISO, SOC 2, and other active certificates with validity dates' },
          { key: 'show_frameworks', label: 'Compliance frameworks', desc: 'Assigned frameworks with status and compliance scores' },
          { key: 'show_compliance_score', label: 'Overall compliance score', desc: 'Aggregated compliance percentage across all frameworks' },
          { key: 'show_last_audit_date', label: 'Last audit date', desc: 'Most recent audit or certification date' },
          { key: 'show_controls_count', label: 'Certified controls count', desc: 'Number of controls that have been certified' },
        ].map(opt => (
          <label key={opt.key} className="flex items-start gap-3 cursor-pointer p-2 rounded-lg hover:bg-slate-50 transition-colors">
            <input
              type="checkbox"
              checked={!!(settings as any)[opt.key]}
              onChange={e => setSettings(s => ({ ...s, [opt.key]: e.target.checked }))}
              className="w-4 h-4 accent-emerald-600 mt-0.5"
            />
            <div>
              <span className="text-sm font-medium text-slate-700">{opt.label}</span>
              <span className="block text-xs text-slate-400">{opt.desc}</span>
            </div>
          </label>
        ))}
      </div>

      {/* Display Options — Sustainability & Emissions */}
      <div className="bg-white border rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <h2 className="font-semibold text-slate-800">Sustainability & Emissions</h2>
          <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">ESG</span>
        </div>
        <p className="text-xs text-slate-500 mb-3">Showcase your environmental commitments and progress to prospects. Powerful for SBTi and net-zero reporting.</p>
        {[
          { key: 'show_sustainability_score', label: 'Sustainability score', desc: 'Composite score from emission reduction progress, offsets, and credits' },
          { key: 'show_emission_targets', label: 'Emission reduction targets', desc: 'Show progress toward Scope 1, 2, 3 reduction goals with visual progress bars' },
          { key: 'show_scope_breakdown', label: 'Scope 1/2/3 breakdown', desc: 'Emissions breakdown by scope from your latest published report' },
          { key: 'show_carbon_credits', label: 'Carbon credits', desc: 'Active carbon credits by type (VCS, Gold Standard, CDM, etc.)' },
          { key: 'show_emission_offsets', label: 'Emission offsets', desc: 'Offset portfolio with registry breakdown and verification status' },
        ].map(opt => (
          <label key={opt.key} className="flex items-start gap-3 cursor-pointer p-2 rounded-lg hover:bg-slate-50 transition-colors">
            <input
              type="checkbox"
              checked={!!(settings as any)[opt.key]}
              onChange={e => setSettings(s => ({ ...s, [opt.key]: e.target.checked }))}
              className="w-4 h-4 accent-emerald-600 mt-0.5"
            />
            <div>
              <span className="text-sm font-medium text-slate-700">{opt.label}</span>
              <span className="block text-xs text-slate-400">{opt.desc}</span>
            </div>
          </label>
        ))}
      </div>

      {/* Customisation */}
      <div className="bg-white border rounded-xl p-5 space-y-4">
        <h2 className="font-semibold text-slate-800">Customisation</h2>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Headline</label>
          <input
            value={settings.custom_headline || ''}
            onChange={e => setSettings(s => ({ ...s, custom_headline: e.target.value }))}
            placeholder="e.g. Our Commitment to Security & Compliance"
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
          <textarea
            value={settings.custom_description || ''}
            onChange={e => setSettings(s => ({ ...s, custom_description: e.target.value }))}
            rows={3}
            placeholder="A brief statement about your security and compliance posture..."
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Accent Colour</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={settings.accent_color || '#059669'}
              onChange={e => setSettings(s => ({ ...s, accent_color: e.target.value }))}
              className="w-10 h-10 rounded cursor-pointer border"
            />
            <input
              value={settings.accent_color || '#059669'}
              onChange={e => setSettings(s => ({ ...s, accent_color: e.target.value }))}
              className="border rounded-lg px-3 py-2 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 font-medium">
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default TrustPortalSettings;
